import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

const FILTERS = [["active", "Actifs"], ["archived", "Archivés"], ["all", "Tous"]];

export default function FormTemplates() {
  const { confirm } = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = () => api.get("/admin/form-templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const visible = templates.filter((t) => filter === "all" || t.status === filter);

  const archive = async (t, archived) => {
    await api.put(`/admin/form-templates/${t.id}/archive`, { archived });
    toast.success(archived ? "Modèle archivé" : "Modèle réactivé");
    load();
  };
  const duplicate = async (t) => { await api.post(`/admin/form-templates/${t.id}/duplicate`); toast.success("Modèle dupliqué"); load(); };
  const del = async (t) => {
    if (!(await confirm({ title: `Supprimer le modèle « ${t.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/form-templates/${t.id}`); toast.success("Modèle supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };

  return (
    <div>
      <PageTitle overline="Automatisations" title="Modèles de formulaires"
        action={<Link to="/admin/automations-formulaires/new" data-testid="new-form-template-btn"
          className="bg-bordeaux text-white rounded-sm px-4 py-2 text-sm hover:bg-bordeaux-hover flex items-center gap-1.5">
          <Icons.Plus size={16} /> Nouveau modèle
        </Link>} />
      <p className="text-sm text-marine/80 mb-4">Créez des formulaires réutilisables (sections et questions) à attacher aux étapes de vos projets, en remplacement de Tally.</p>

      <div className="flex gap-2 mb-5" role="group" aria-label="Filtrer par statut">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}`} aria-pressed={filter === v}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${filter === v ? "bg-marine text-white border-marine" : "border-marine/25 text-marine hover:bg-marine/5"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="form-templates-grid">
        {visible.map((t) => {
          const nQuestions = t.sections.reduce((n, s) => n + s.questions.length, 0);
          return (
            <div key={t.id} data-testid={`form-template-card-${t.id}`} className="bg-sable rounded-sm border border-marine/10 p-5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-display text-lg text-marine">{t.name}</div>
                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm border shrink-0 ${t.status === "active" ? "bg-bleu-50 text-bleu border-bleu/30" : "bg-sable-clair text-gris-texte border-gris-texte/30"}`}>
                  {t.status === "active" ? "Actif" : "Archivé"}
                </span>
              </div>
              <div className="text-xs text-marine/80 mb-3">{t.sections.length} section(s) · {nQuestions} question(s)</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {t.sections.map((s) => <span key={s.id} className="text-[9px] bg-marine-50 text-marine border border-marine/30 px-2 py-0.5 rounded-sm">{s.title || "(sans titre)"}</span>)}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Link to={`/admin/automations-formulaires/${t.id}`} data-testid={`form-template-edit-${t.id}`}
                  className="text-xs border border-marine/25 text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">
                  <Icons.Pencil size={16} /> Modifier
                </Link>
                <button onClick={() => duplicate(t)} data-testid={`form-template-duplicate-${t.id}`}
                  className="text-xs border border-marine/25 text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">
                  <Icons.Copy size={16} /> Dupliquer
                </button>
                {t.status === "active" ? (
                  <button onClick={() => archive(t, true)} data-testid={`form-template-archive-${t.id}`}
                    className="text-xs border border-marine/30 text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">
                    <Icons.Archive size={16} /> Archiver
                  </button>
                ) : (
                  <button onClick={() => archive(t, false)} data-testid={`form-template-unarchive-${t.id}`}
                    className="text-xs border border-marine/30 text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">
                    <Icons.ArchiveRestore size={16} /> Réactiver
                  </button>
                )}
                <button onClick={() => del(t)} data-testid={`form-template-delete-${t.id}`}
                  aria-label={`Supprimer le modèle ${t.name}`} className="text-xs text-rouge hover:bg-rouge/10 rounded-sm p-1.5">
                  <Icons.Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <p className="text-sm text-marine/80">Aucun modèle{filter !== "all" ? ` (${FILTERS.find(([v]) => v === filter)[1].toLowerCase()})` : ""}.</p>
        )}
      </div>
    </div>
  );
}
